import {Injectable} from "@angular/core";
import {VisualDoorRenderer} from "./visual-door-renderer";
import {CanvasCache, CanvasType} from "../caches/canvas-cache";
import {ShadowInpaintingRenderer} from "./shadow-inpainting-renderer";
import {ClippedCorners, Corners, DoorCorners, LightPosition, Point, ProductsMap, RenderOptions, Size} from "../types";

export interface RenderedScenario {
  image: HTMLImageElement
  positionInCanvas: Point
  corners: Array<DoorCorners>
  size: Size
  lightPosition: LightPosition
  shadowImageUrl?: string
}

@Injectable({
  providedIn: 'root'
})
export class ScenarioRenderer {
  constructor(
    private readonly canvasCache: CanvasCache,
    private readonly visualDoorRenderer: VisualDoorRenderer,
    private readonly shadowInpaintingRenderer: ShadowInpaintingRenderer
  ) {
  }

  public async renderScenario(
    {canvas, scenario, products}: {
      canvas: HTMLCanvasElement,
      scenario: RenderedScenario,
      products: ProductsMap,
    }, options: RenderOptions): Promise<void> {

    // Create a canvas which represents the current scenario
    console.time('- create canvas')
    const {context: temporaryContext, canvas: temporaryCanvas} = this.canvasCache.get(CanvasType.SCENARIO, {
      width: scenario.image.width,
      height: scenario.image.height
    })
    console.timeEnd('- create canvas')

    // Draw the scenario image on the canvas
    temporaryContext.drawImage(scenario.image, 0, 0);

    if (options.showDoor) {
      const cornersWithSelectedDoor = scenario.corners.filter(c => !!products.get(c.index)?.door)

      // Draw doors
      for (const corners of cornersWithSelectedDoor) {
        await this.visualDoorRenderer.renderVisualDoor({
          scenario,
          renderingContext: temporaryContext,
          corners,
          products: products.get(corners.index),
        }, options)
      }

      // Draw scenario shadow image
      if (options.enableShadowInpainting) {
        console.time('- Draw scenario shadow image')
        await this.shadowInpaintingRenderer.drawScenarioShadowImage({
          renderingContext: temporaryContext,
          doorCorners: cornersWithSelectedDoor[0],
          scenario,
          renderOptions: options
        })
        console.timeEnd('- Draw scenario shadow image')
      }
    }

    // Draw the final image on the outer canvas
    console.time('- Draw scenario image')
    const context = canvas.getContext('2d')!;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(temporaryCanvas, scenario.positionInCanvas.x, scenario.positionInCanvas.y, scenario.size.width, scenario.size.height);
    console.timeEnd('- Draw scenario image')

  }

  /**
   * Clips the rendering context to the corners of the door
   * Followed drawing will only be visible within the clipped area
   */
  public static clipCorners({renderingContext, corners}: {
    renderingContext: CanvasRenderingContext2D,
    corners: Corners & ClippedCorners
  }) {
    this.drawCornersPath({renderingContext, corners})
    renderingContext.clip();
  }

  /**
   * Draws a path around the corners of the door
   */
  public static drawCornersPath({renderingContext, corners}: {
    renderingContext: CanvasRenderingContext2D,
    corners: Corners & ClippedCorners
  }) {
    renderingContext.beginPath();
    if (corners.hasClippedCorners) {
      renderingContext.moveTo(corners.topLeftBottom!.x, corners.topLeftBottom!.y);
      renderingContext.lineTo(corners.topLeftRight!.x, corners.topLeftRight!.y);
      renderingContext.lineTo(corners.topRightLeft!.x, corners.topRightLeft!.y);
      renderingContext.lineTo(corners.topRightBottom!.x, corners.topRightBottom!.y);
      renderingContext.lineTo(corners.bottomRight.x, corners.bottomRight.y);
      renderingContext.lineTo(corners.bottomLeft.x, corners.bottomLeft.y);
      renderingContext.lineTo(corners.topLeftBottom!.x, corners.topLeftBottom!.y);
    } else {
      // Add each door's path to the combined path
      renderingContext.moveTo(corners.topLeft.x, corners.topLeft.y);
      renderingContext.lineTo(corners.topRight.x, corners.topRight.y);
      renderingContext.lineTo(corners.bottomRight.x, corners.bottomRight.y);
      renderingContext.lineTo(corners.bottomLeft.x, corners.bottomLeft.y);
      renderingContext.lineTo(corners.topLeft.x, corners.topLeft.y);
    }
    renderingContext.closePath();
  }
}
